import { useAtomValue, useSetAtom } from "jotai";
import { useRef, useEffect } from "react";
import classNames from "classnames";
import {
  sideBarSelectedAtom,
  sideBarWidthAtom,
  SUMMARY,
  sidebarAnimationAtom,
  isBlankCanvasAtom,
  isDraftModeIncludingTemplatesAtom,
  nodesAtom,
} from "@zapier/canvas-state";
import { PRODUCT_NAV_WIDTH } from "@zapier/canvas-constants";
import capitalizeFirstLetter from "../../utils/capitalizeFirstLetter";
import { SummaryPanel } from "../SummaryPanel";
import DevTools from "../DevTools/DevTools";
import styles from "./SidebarDrawer.module.css";
import { CopilotChatPanel } from "@zapier/ai-toolkit/client";
import { IconButton } from "@zapier/design-system";
import { getCopilotSuggestions } from "@zapier/copilot-tools";

type SidebarDrawerProps = {
  onCloseClick: () => void;
};

export default function SidebarDrawer({ onCloseClick }: SidebarDrawerProps) {
  const sideBarSelected = useAtomValue(sideBarSelectedAtom);
  const sidebarAnimation = useAtomValue(sidebarAnimationAtom);
  const setSideBarWidth = useSetAtom(sideBarWidthAtom);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Atoms needed for copilot suggestions
  const isBlankCanvas = useAtomValue(isBlankCanvasAtom);
  const isDraftModeIncludingTemplates = useAtomValue(
    isDraftModeIncludingTemplatesAtom,
  );
  const nodes = useAtomValue(nodesAtom);

  // Generate suggestions based on canvas state
  const copilotSuggestions = getCopilotSuggestions({
    isBlankCanvas,
    isDraftModeIncludingTemplates,
    nodes,
  });

  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    // TODO: This is a hack to get the width of the drawer.
    // We should fix the bug in AI-Toolkit to make sure that CopilotChatPanel
    // doesn't change the width of the drawer.
    const findAndObserveDrawer = () => {
      // Find the actual drawer element (the one that has the real width)
      const drawerElement =
        document.querySelector('[class*="BaseDrawer"]') ||
        document.querySelector('[class*="drawer"]') ||
        document.querySelector("aside");

      if (drawerElement) {
        // Set up ResizeObserver
        resizeObserver = new ResizeObserver(() => {
          const width = drawerElement.getBoundingClientRect().width;
          setSideBarWidth(PRODUCT_NAV_WIDTH + width);
        });

        resizeObserver.observe(drawerElement);

        // Initial measurement
        const width = drawerElement.getBoundingClientRect().width;
        setSideBarWidth(PRODUCT_NAV_WIDTH + width);
      } else {
        // Fallback to reasonable default
        setSideBarWidth(PRODUCT_NAV_WIDTH + 350);
      }
    };

    if (sideBarSelected) {
      timeoutId = setTimeout(findAndObserveDrawer, 50);
    } else {
      setSideBarWidth(PRODUCT_NAV_WIDTH);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [sideBarSelected, setSideBarWidth]);

  const SIDEBAR_CONTENT = {
    [SUMMARY]: {
      title: capitalizeFirstLetter(SUMMARY),
      component: (
        <>
          <div className={styles.titleContainer}>
            <div>{capitalizeFirstLetter(SUMMARY)}</div>
            <IconButton
              name="arrowSmallLeft"
              variant="tertiary"
              size="small"
              onPress={onCloseClick}
            />
          </div>
          <div className={styles.componentContainer}>
            <SummaryPanel />
          </div>
        </>
      ),
    },
    devTools: {
      title: "🔒 DevTools - staff only (⌘D)",
      component: (
        <>
          <div className={styles.titleContainer}>
            <div>🔒 DevTools - staff only (⌘D)</div>{" "}
            <IconButton
              name="arrowSmallLeft"
              variant="tertiary"
              size="small"
              onPress={onCloseClick}
            />
          </div>
          <div className={styles.componentContainer}>
            <DevTools />
          </div>
        </>
      ),
    },
    copilot: {
      title: "Copilot",
      component: (
        <div
          className={classNames(styles.copilotChatPanelContainer, {
            [styles.fadeIn]: sidebarAnimation === "fadeIn",
          })}
        >
          <CopilotChatPanel
            placeholder="Ask me anything about your canvas..."
            onCloseClick={onCloseClick}
            emptyStateSuggestions={copilotSuggestions}
          />
        </div>
      ),
    },
  };

  const selectedSidebar =
    sideBarSelected &&
    SIDEBAR_CONTENT[sideBarSelected as keyof typeof SIDEBAR_CONTENT]
      ? SIDEBAR_CONTENT[sideBarSelected as keyof typeof SIDEBAR_CONTENT]
      : {
          title: "",
          component: null,
        };

  return (
    <div ref={drawerRef} className={styles.drawerContainer}>
      {selectedSidebar.component}
    </div>
  );
}
